import { Button, Flex } from '@aws-amplify/ui-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { InstitutionsResults } from '../components/InstitutionsResults';
import QueryEditor from '../custom-components/QueryEditor';
import { SearchHistoryPanel } from '../custom-components/SearchHistoryPanel';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { useSearchHistory } from '../hooks/useSearchHistory';
import General from '../utils/GeneralUtils';
import { InstitutionsSearchParams } from '../utils/InstitutionsSearchParams';
import { convertQtoQuery } from '../utils/queryConverter';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';

export function Institutions() {
  const globalContext = useContext(GlobalContext);
  const institution = globalContext.environment.entities.institution;

  General.setTitle(document, institution.namePlural, globalContext);

  const location = useLocation();
  const navigate = useNavigate();

  const [currentSearchParams, setCurrentSearchParams] = useState<InstitutionsSearchParams>();
  const [newSearchParams, setNewSearchParams] = useState<InstitutionsSearchParams>();
  const [isSearchHistoryPanelOpen, setIsSearchHistoryPanelOpen] = useState(
    LocalStorageService.get<boolean>(LocalStorageKeys.IS_INSTITUTIONS_SEARCH_HISTORY_PANEL_OPEN)
  );

  // Search history hook - pass userId for Pusher subscription
  const userId = globalContext.authInfo?.userAttributes?.sub || '';
  const teamId = globalContext.authInfo?.teamId || '';
  const searchHistory = useSearchHistory({ entity: 'INSTITUTION', teamId, source: 'INSTITUTIONS_SEARCH', userId });

  function handleOnSearch() {
    navigate(`?${newSearchParams?.serialize('1')}`);
  }

  // The URL's q and the conditions it runs, from the URL directly, so the list and its page
  // change together.
  const q = useMemo(
    () => String(General.getQueryParamValueOrNull(new URLSearchParams(location.search), 'q') || ''),
    [location.search]
  );
  const conditions = useMemo(() => convertQtoQuery(q), [q]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const params = {
      q: General.getQueryParamValueOrNull(queryParams, 'q'),
      sortBy: General.getQueryParamValueOrNull(queryParams, 'sortBy'),
      sortDir: General.getQueryParamValueOrNull(queryParams, 'sortDir'),
      page: General.getQueryParamValueOrNull(queryParams, 'page'),
      pageToken: General.getQueryParamValueOrNull(queryParams, 'pageToken'),
    }
    setCurrentSearchParams(new InstitutionsSearchParams(params))
    setNewSearchParams(new InstitutionsSearchParams(params))
  }, [location.search]);

  function handleToggleSearchHistoryPanel() {
    const newValue = !isSearchHistoryPanelOpen;
    setIsSearchHistoryPanelOpen(newValue);
    LocalStorageService.set(LocalStorageKeys.IS_INSTITUTIONS_SEARCH_HISTORY_PANEL_OPEN, newValue);
  }

  return (
    <GlobalContext.Consumer>
      {
        (globalContextProps: GlobalContextProps) => <>
          <div style={{ display: 'flex', minHeight: '100vh' }}>
            <SearchHistoryPanel
              basePath="/directory/institutions"
              teamShared={searchHistory.teamShared}
              starred={searchHistory.starred}
              recent={searchHistory.recent}
              currentUserId={userId}
              onToggleStar={searchHistory.toggleStar}
              onArchive={searchHistory.archiveSearch}
              onRename={searchHistory.renameSearch}
              onShareWithTeam={searchHistory.shareWithTeam}
              onUnshareFromTeam={searchHistory.unshareFromTeam}
              isOpen={isSearchHistoryPanelOpen}
              onTogglePanel={handleToggleSearchHistoryPanel}
            />
            <div style={{ flex: 1, overflow: 'auto', position: 'relative', zIndex: 1 }}>
              <div className="content content-1000">
                <div className="main-card">
                  <div style={{ padding: 10 }}>
                    {
                      currentSearchParams
                        ? <QueryEditor
                            initialQuery={currentSearchParams?.q.serialize()}
                            teamTags={[]}
                            type='institutions'
                            supportedFields={institution.queryEditorSupportedFields}
                            onChange={(query: string | null) => {
                              setNewSearchParams(prevState => new InstitutionsSearchParams({
                                ...prevState?.raw(),
                                ...{ q: query }
                              }))
                            }}
                          />
                        : <></>
                    }
                    <Flex justifyContent='flex-end' marginTop={15} gap={5}>
                      <Button
                        variation='primary'
                        size='small'
                        isDisabled={currentSearchParams?.serialized === newSearchParams?.serialized}
                        onClick={handleOnSearch}
                        loadingText="Searching"
                      >Search</Button>
                    </Flex>
                  </div>
                </div>
              </div>

              <InstitutionsResults
                conditions={conditions}
                searchKey={q}
                source="INSTITUTIONS_SEARCH"
                savedSearchId={searchHistory.currentSearchId}
              />
            </div>
          </div>
        </>
      }
    </GlobalContext.Consumer>
  )
}
